import React from 'react';
import FormSkeleton from '../Skeletons/FormSkeleton';

/**
 * Reusable wrapper for forms with loading, error, and glassmorphism styling
 * @param {Object} props
 * @param {boolean} props.isLoading - Whether data is loading
 * @param {Error} props.error - Error object if fetch failed
 * @param {any} props.data - The data to pass to children
 * @param {React.ReactNode} props.children - Form component to render
 * @param {string} props.errorMessage - Custom error message
 * @param {string} props.className - Additional CSS classes
 */
const FormWrapper = ({
  isLoading,
  error,
  data,
  children,
  tabId,
  errorMessage = 'Failed to load data',
  className = '',
}) => {
  const cardStyle = {
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
  };

  // Show skeleton loader while loading
  if (isLoading) {
    return (
      <div
        className={`rounded-lg shadow-sm p-6 ${className}`}
        style={cardStyle}
        tabId={tabId}
      >
        <FormSkeleton />
      </div>
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <div className={`rounded-lg shadow-sm p-6 ${className}`} style={cardStyle}>
        <div className='text-center py-8'>
          <p className='text-red-600 font-medium'>{errorMessage}</p>
          {error.message && (
            <p className='text-sm mt-2' style={{ color: 'var(--text-muted)' }}>
              {error.message}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Show "no data" state if data is null/undefined
  if (!data) {
    return (
      <div className={`rounded-lg shadow-sm p-6 ${className}`} style={cardStyle}>
        <div className='text-center py-8'>
          <p style={{ color: 'var(--text-body)' }}>No data available</p>
        </div>
      </div>
    );
  }

  // Render children with data
  return <div className={`${className}`}>{children}</div>;
};

export default FormWrapper;
